import React, { useEffect, useState } from "react";
import { Route, useRouteMatch, Switch, useParams } from "react-router-dom";
import moment from "moment";
import { ProductCreateContainer } from "./ProductCreateContainer/ProductCreateContainer";
import { ProductDetailsContainer } from "./ProductDetailsContainer/ProductDetailsContainer";
import { ProductListContainer } from "./ProductListContainer/ProductListContainer";
import { checkNotLoaded, toHttpFormat, toHttpFormatStartOfDay } from "../../lib/utils";
import { useAppDispatch, useAppSelector } from "../..";
import {
  fetchAgrStatus,
  fetchChargeNames,
  fetchCurrenciesDic,
  fetchPeriodTypes,
  fetchPriceUnits,
  fetchProducts,
  fetchProfileTypeStatus,
  fetchPaymentType,
  fetchInvoicePeriodStatus,
  fetchPrePaymentPeriodStatus,
  fetchRateType,
  fetchServiceStatus,
  fetchTariffModelTypes,
  fetchTariffPeriodAlignments,
  fetchTariffTypes,
  fetchRateModes,
  fetchRetailNetworks,
  fetchServiceUsageDocumentType,
  fetchServiceUsageDirection,
} from "../../store/slices/rtkDictionaries";
import { fetchGetAgreementsByAccId } from "../../store/slices/agreements";
import { fetchServiceTypeList } from "../../store/slices/offering/offering";
import {
  fetchLifespanPeriods,
  fetchOfferingAccessTypes,
  fetchOfferingProlongTypes,
  fetchOfferingStatuses,
  fetchBillPeriods,
  fetchBundleTypes,
} from "../../store/slices/dicts";
import "./AccountProductsContainer.less";
import { fetchCommonServiceProfiles } from "../../store/slices/service-profile";
import { ServiceListFilterValues } from "./ProductListContainer/ProductListContainerInterface";
import { Agreement } from "../../lib/interfaces/Agreement";
import {
  fetchProductVersions,
  fetchProductsByAggId,
  productsActions,
} from "../../store/slices/products/products";
import { fetchServiceTypes } from "../../store/slices/services";

export const AccountProductsContainer = () => {
  const { path } = useRouteMatch();
  const dispatch = useAppDispatch();
  const { accountId } = useParams<{ accountId?: string }>();

  const {
    agrStatusLoading,
    productsLoading,
    serviceStatusLoading,
    tariffModelTypesLoading,
    periodTypesLoading,
    tariffPeriodAlignmentsLoading,
    dicCurrenciesLoading,
    priceUnitsLoading,
    tariffTypesLoading,
    rateTypeLoading,
    chargeNamesLoading,
    profileTypeStatus,
    profileTypeStatusLoading,
    paymentTypeLoading,
    invoicePeriodStatusLoading,
    prePaymentPeriodStatusLoading,
    rateModesEventsLoading,
    retailNetworksLoading,
    serviceUsageDocumentTypeLoading,
    serviceUsageDirectionLoading,
  } = useAppSelector((state) => state.rtkDictionaries);
  const {
    offeringStatusesLoading,
    offeringProlongTypesLoading,
    offeringAccessTypesLoading,
    lifespanPeriodsLoading,
    billPeriodsLoading,
    offeringBundleTypesLoading,
  } = useAppSelector((state) => state.dicts);
  const { agreements, agreementsLoading } = useAppSelector((state) => state.agreements);
  const { serviceTypeListLoading } = useAppSelector((state) => state.offering);
  const { serviceTypesFetchingState } = useAppSelector((state) => state.service);

  useEffect(() => {
    return () => {
      dispatch(productsActions.cleanProductList());
    };
  }, [dispatch]);

  // Загрузка договоров
  useEffect(() => {
    if (checkNotLoaded(agreementsLoading)) {
      // @ts-ignore
      dispatch(fetchGetAgreementsByAccId({ accId: accountId }));
    }
  }, [accountId, agreementsLoading, dispatch]);

  // Загрузка справочников
  useEffect(() => {
    if (checkNotLoaded(agrStatusLoading)) dispatch(fetchAgrStatus());
    if (checkNotLoaded(productsLoading)) dispatch(fetchProducts());
    if (checkNotLoaded(serviceTypeListLoading)) {
      dispatch(
        fetchServiceTypeList({
          onDate: toHttpFormatStartOfDay(moment()),
          includeFutureDates: true,
          includePastDates: false,
        })
      );
    }
    if (checkNotLoaded(offeringStatusesLoading)) dispatch(fetchOfferingStatuses());
    if (checkNotLoaded(serviceStatusLoading)) dispatch(fetchServiceStatus());
    if (checkNotLoaded(offeringProlongTypesLoading)) dispatch(fetchOfferingProlongTypes());
    if (checkNotLoaded(offeringAccessTypesLoading)) dispatch(fetchOfferingAccessTypes());
    if (checkNotLoaded(lifespanPeriodsLoading)) dispatch(fetchLifespanPeriods());
    if (checkNotLoaded(tariffModelTypesLoading)) dispatch(fetchTariffModelTypes());
    if (checkNotLoaded(periodTypesLoading)) dispatch(fetchPeriodTypes());
    if (checkNotLoaded(tariffPeriodAlignmentsLoading)) dispatch(fetchTariffPeriodAlignments());
    if (checkNotLoaded(dicCurrenciesLoading)) dispatch(fetchCurrenciesDic());
    if (checkNotLoaded(priceUnitsLoading)) dispatch(fetchPriceUnits());
    if (checkNotLoaded(tariffTypesLoading)) dispatch(fetchTariffTypes());
    if (checkNotLoaded(rateTypeLoading)) dispatch(fetchRateType());
    if (checkNotLoaded(chargeNamesLoading)) dispatch(fetchChargeNames());
    if (checkNotLoaded(profileTypeStatusLoading)) dispatch(fetchProfileTypeStatus());
    if (checkNotLoaded(paymentTypeLoading)) dispatch(fetchPaymentType());
    if (checkNotLoaded(invoicePeriodStatusLoading)) dispatch(fetchInvoicePeriodStatus());
    if (checkNotLoaded(prePaymentPeriodStatusLoading)) dispatch(fetchPrePaymentPeriodStatus());
    if (checkNotLoaded(billPeriodsLoading)) dispatch(fetchBillPeriods());
    if (checkNotLoaded(offeringBundleTypesLoading)) dispatch(fetchBundleTypes());
    if (checkNotLoaded(serviceTypesFetchingState)) dispatch(fetchServiceTypes());
    if (checkNotLoaded(rateModesEventsLoading)) dispatch(fetchRateModes());
    dispatch(fetchCommonServiceProfiles());
    if (checkNotLoaded(retailNetworksLoading)) dispatch(fetchRetailNetworks());
    if (checkNotLoaded(serviceUsageDocumentTypeLoading)) dispatch(fetchServiceUsageDocumentType());
    if (checkNotLoaded(serviceUsageDirectionLoading)) dispatch(fetchServiceUsageDirection());
  }, []);

  const [serviceListFilterValues, setServiceListFilterValues] = useState<ServiceListFilterValues>({
    isSearchIncludePastDates: false,
    onDate: moment().startOf("day"),
    currentSortOrder: { fromDate: "ascend" },
  });

  const handleSetServiceListFilterValues = (prop) => {
    setServiceListFilterValues((prev) => ({ ...prev, ...prop }));
  };

  const loadProductVersions = ({ id }) => {
    dispatch(fetchProductVersions(id));
  };

  const loadProducts = ({ onDate, isSearchIncludePastDates } = serviceListFilterValues) => {
    const updateData = async () => {
      if (agreements.length !== 0) {
        const aggSet = new Set<number>();
        (agreements as Agreement[]).forEach((item) => {
          aggSet.add(item.id);
        });
        const includePastDates = onDate ? isSearchIncludePastDates : false;
        const isFutureDateIncluded = includePastDates
          ? false
          : !moment(onDate).isBefore(moment().startOf("day"));
        const bodyRequest: {
          agreementsIds: number[];
          futureDates: boolean;
          onDate?: string;
          includePastDates: boolean;
          extendedInfo: boolean;
        } = {
          agreementsIds: [...aggSet],
          futureDates: isFutureDateIncluded,
          includePastDates,
          extendedInfo: false,
        };
        if (onDate) {
          bodyRequest.onDate = toHttpFormat(onDate);
        }
        await dispatch(fetchProductsByAggId(bodyRequest));
      }
    };
    updateData();
  };

  return (
    <div className="account-products-container">
      <Switch>
        <Route path={`${path}/create`}>
          <ProductCreateContainer />
        </Route>
        <Route path={`${path}/:productId`}>
          <ProductDetailsContainer />
        </Route>
        <Route path={`${path}`}>
          <ProductListContainer
            loadProducts={loadProducts}
            loadProductVersions={loadProductVersions}
            serviceListFilterValues={serviceListFilterValues}
            handleSetServiceListFilterValues={handleSetServiceListFilterValues}
          />
        </Route>
      </Switch>
    </div>
  );
};
